import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ExternalLink, Instagram, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { AlienLogo } from "@/components/AlienLogo";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Countdown } from "@/components/Countdown";
import { Gallery } from "@/components/Gallery";
import { MerchCard } from "@/components/MerchCard";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" } }),
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

// Render a config text with \n → <br>
const multiline = (text) => {
  if (!text) return null;
  const parts = String(text).split(/\r?\n/);
  return parts.map((p, i) => (
    <span key={i}>{p}{i < parts.length - 1 && <br />}</span>
  ));
};

export default function Home() {
  const [members, setMembers] = useState([]);
  const [lives, setLives] = useState([]);
  const [merch, setMerch] = useState([]);
  const [cfg, setCfg] = useState(null); // null = not loaded yet

  useEffect(() => {
    Promise.all([
      api.get("/members"),
      api.get("/lives", { params: { upcoming: true } }),
      api.get("/merch"),
      api.get("/site-config"),
    ])
      .then(([m, l, mr, c]) => { setMembers(m.data); setLives(l.data); setMerch(mr.data); setCfg(c.data); })
      .catch(() => { setCfg({}); });
  }, []);

  // Preload the hero image as soon as we know its URL to eliminate the second flash
  useEffect(() => {
    if (cfg?.hero_image_url) {
      const link = document.createElement("link");
      link.rel = "preload"; link.as = "image"; link.href = cfg.hero_image_url;
      link.fetchPriority = "high";
      document.head.appendChild(link);
      return () => { try { document.head.removeChild(link); } catch { /* already removed */ } };
    }
  }, [cfg?.hero_image_url]);

  const c = cfg || {};

  return (
    <div className="grain min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen py-24 md:py-0 md:h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          {cfg && cfg.hero_image_url ? (
            <img
              src={cfg.hero_image_url}
              alt=""
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover object-[center_20%] md:object-[80%_center] lg:object-[70%_center]"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0A0A0A] via-[#151515] to-[#050505]" />
          )}
          {/* Base subtle dark tint so the photo stays visible but readable */}
          <div className="absolute inset-0 bg-black/30" />
          {/* Left-to-right dark gradient — anchors text side, keeps faces visible on the right (desktop/tablet) */}
          <div className="hidden md:block absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.7) 30%, rgba(10,10,10,0.25) 60%, rgba(10,10,10,0) 90%)" }} />
          {/* Mobile: darken vertically so top (tagline) and bottom (marquee) are readable while faces peek in the middle */}
          <div className="md:hidden absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.5) 40%, rgba(10,10,10,0.4) 70%, rgba(10,10,10,0.95) 100%)" }} />
          {/* Bottom fade toward the marquee bar so it's always readable */}
          <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.9) 100%)" }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 w-full">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0} className="flex items-center gap-3 mb-6">
            <AlienLogo size={56} />
            <span className="uppercase tracking-[0.4em] text-[color:var(--alien)] text-xs md:text-sm">{cfg?.hero_tagline || "Dal Lago di Garda · Verona"}</span>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl leading-[0.9] text-white neon-text"
          >
            TOO<br />
            MUCH<br />
            <span className="text-[color:var(--alien)]">NOISE.</span>
          </motion.h1>

          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2} className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed">
            {cfg?.hero_intro || "Siamo una band pop punk nata a Verona, con influenze dei primi anni 2000. Le nostre canzoni raccontano momenti di vita quotidiana con un linguaggio semplice e diretto, senza troppe pose, ma con l'energia giusta per restare in testa."}
          </motion.p>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="mt-10 flex flex-wrap gap-4">
            <a
              href={cfg?.single_spotify_url || "#"}
              target="_blank" rel="noreferrer"
              data-testid="hero-cta-spotify"
              className="inline-flex items-center gap-2 bg-[color:var(--spotify)] text-white font-bold px-6 py-3 uppercase tracking-widest text-sm hover:bg-[color:var(--spotify-hover)]"
            >
              <PlayCircle size={18} /> Ascolta su Spotify
            </a>
            <a
              href="#live"
              data-testid="hero-cta-live"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 uppercase tracking-widest text-sm hover:border-[color:var(--alien)] hover:text-[color:var(--alien)]"
            >
              Prossimi Live
            </a>
          </motion.div>

          {lives[0] && (
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4} className="mt-10">
              <Countdown target={lives[0].date} venue={`${lives[0].venue}${lives[0].city ? ` · ${lives[0].city}` : ""}`} />
            </motion.div>
          )}
        </div>

        {/* Marquee */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-t border-white/10 bg-black/40 backdrop-blur-sm py-3">
          <div className="marquee-inner flex whitespace-nowrap gap-12 font-display text-2xl tracking-widest text-white/30">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="flex items-center gap-4">
                SEGUICI SU INSTAGRAM <span className="text-[color:var(--alien)]">◆</span> @toomuchnoise_ig <span className="text-[color:var(--alien)]">◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* BAND */}
      <section id="band" className="relative py-24 md:py-32 px-5 md:px-10" data-testid="band-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div className="flex items-end gap-5">
              <div>
                <p className="text-[color:var(--alien)] uppercase tracking-[0.4em] text-xs mb-3">01 — La Band</p>
                <h2 className="font-display text-5xl md:text-7xl">{multiline(cfg?.band_title || "LA SQUADRA\nDEI TOO MUCH")}</h2>
              </div>
              {cfg && (
                <motion.img
                  src={cfg.band_logo_url || "https://customer-assets-jt897jd0.emergentagent.net/job_noise-preview/artifacts/jyfqy4l1_Logo%20Sticker%20%281%29.png"}
                  alt="Too Much Noise band logo"
                  loading="lazy"
                  decoding="async"
                  initial={{ opacity: 0, scale: 0.6, rotate: 20 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: -6 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
                  className="logo-sticker w-24 md:w-32 lg:w-40 shrink-0"
                  data-testid="band-logo-sticker"
                />
              )}
            </div>
            <p className="text-white/60 max-w-md text-base whitespace-pre-line">
              {cfg?.band_intro || "Dalla nostra nascita nel 2024, grazie alla passione, alla dedizione e alla voglia di portare la nostra musica sempre più lontano, abbiamo avuto l'opportunità di esibirci in numerosi locali ed eventi musicali della provincia di Verona, arrivando anche a Mantova e Brescia. Palco dopo palco, abbiamo costruito il nostro percorso portando ovunque la nostra energia, tra pop punk, rock e grandi hit italiane e internazionali."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {members.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="group relative border border-white/10 bg-[color:var(--bg-panel)] overflow-hidden hover:border-[color:var(--noise-green)]"
                data-testid={`member-card-${i}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={m.image_url} alt={m.name} className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <p className="text-[color:var(--noise-green)] uppercase text-xs tracking-widest">{m.role}</p>
                  <h3 className="font-display text-2xl mt-1">{m.name}</h3>
                  {m.nickname && <p className="text-white/50 text-sm italic">"{m.nickname}"</p>}
                  <p className="mt-3 text-white/70 text-sm leading-relaxed">{m.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SINGOLO */}
      <section id="singolo" className="relative py-24 md:py-32 px-5 md:px-10 bg-[color:var(--bg-panel)] border-y border-white/10" data-testid="single-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative"
          >
            {cfg && cfg.single_cover_url ? (
              <img
                src={cfg.single_cover_url}
                alt="Cover singolo"
                loading="lazy"
                decoding="async"
                className="w-full aspect-square object-cover border border-white/10"
                data-testid="single-cover-img"
              />
            ) : (
              <div className="w-full aspect-square bg-gradient-to-br from-[#0A0A0A] via-[#151515] to-[#050505] border border-white/10 flex items-center justify-center" data-testid="single-cover-placeholder">
                <span className="font-display text-4xl md:text-6xl text-[color:var(--alien)]/40 tracking-widest">
                  {(cfg?.single_title || "BUONA FORTUNA").toUpperCase()}
                </span>
              </div>
            )}
            <div className="absolute inset-0 pointer-events-none border-2 border-[color:var(--alien)]/0 hover:border-[color:var(--alien)]/60 transition-colors" />
          </motion.div>

          <div>
            <p className="text-[color:var(--alien)] uppercase tracking-[0.4em] text-xs mb-3">02 — I Nostri Pezzi</p>
            <h2 className="font-display text-6xl md:text-8xl leading-none">{cfg?.single_title || "BUONA FORTUNA"}</h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-lg text-lg">
              {cfg?.single_description || "Il nuovo brano dei Too Much Noise. Distorsione, malinconia e un ritornello che ti resterà in testa."}
            </p>
            <a
              href={cfg?.single_spotify_url || "#"}
              target="_blank" rel="noreferrer"
              data-testid="single-spotify-btn"
              className="mt-8 inline-flex items-center gap-3 bg-[color:var(--spotify)] text-white font-bold px-7 py-4 uppercase tracking-widest text-sm hover:bg-[color:var(--spotify-hover)]"
            >
              <PlayCircle size={20} /> Ascolta su Spotify <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* LIVE */}
      <section id="live" className="relative py-24 md:py-32 px-5 md:px-10" data-testid="live-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="text-[color:var(--alien)] uppercase tracking-[0.4em] text-xs mb-3">03 — Prossimi Live</p>
              <h2 className="font-display text-5xl md:text-7xl">{multiline(cfg?.live_title || "SUL\nPALCO")}</h2>
            </div>
            <Link to="/live" data-testid="live-see-all-btn" className="border border-white/40 text-white px-5 py-3 uppercase tracking-widest text-sm hover:border-[color:var(--alien)] hover:text-[color:var(--alien)]">
              Vedi tutti i live →
            </Link>
          </div>

          {lives.length === 0 ? (
            <p className="text-white/50 border border-dashed border-white/15 p-10 text-center" data-testid="live-empty">
              Nessuna data in arrivo. Ma stiamo tornando presto.
            </p>
          ) : (
            <div className="space-y-3">
              {lives.slice(0, 5).map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="group grid md:grid-cols-[140px_1fr_auto] items-center gap-4 md:gap-8 p-5 md:p-6 border border-white/10 bg-[color:var(--bg-panel)] hover:border-[color:var(--alien)]"
                  data-testid={`live-card-${i}`}
                >
                  <div className="font-display text-3xl md:text-4xl text-[color:var(--alien)]">
                    {formatDate(l.date).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl">{l.venue}</h3>
                    <p className="text-white/60 text-sm flex items-center gap-2 mt-1">
                      <MapPin size={14} /> {l.address}{l.city ? `, ${l.city}` : ""}
                    </p>
                    {l.notes && <p className="text-white/40 text-xs mt-1">{l.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    {l.map_url && (
                      <a href={l.map_url} target="_blank" rel="noreferrer" data-testid={`live-map-btn-${i}`}
                        className="border border-white/30 px-4 py-2 text-xs uppercase tracking-widest hover:border-[color:var(--alien)] hover:text-[color:var(--alien)]">
                        Mappa
                      </a>
                    )}
                    {l.ticket_url && (
                      <a href={l.ticket_url} target="_blank" rel="noreferrer" data-testid={`live-ticket-btn-${i}`}
                        className="bg-[color:var(--alien)] text-black font-bold px-4 py-2 text-xs uppercase tracking-widest">
                        Ticket
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MERCH */}
      <section id="merch" className="relative py-24 md:py-32 px-5 md:px-10 bg-[color:var(--bg-panel)] border-y border-white/10" data-testid="merch-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="text-[color:var(--alien)] uppercase tracking-[0.4em] text-xs mb-3">04 — Merch</p>
              <h2 className="font-display text-5xl md:text-7xl">{multiline(cfg?.merch_title || "MERCH\nTOO MUCH")}</h2>
            </div>
            <a
              href={cfg?.instagram_url || "#"}
              target="_blank" rel="noreferrer"
              data-testid="merch-dm-btn"
              className="inline-flex items-center gap-2 border border-[color:var(--alien)] text-[color:var(--alien)] px-5 py-3 uppercase tracking-widest text-sm hover:bg-[color:var(--alien)] hover:text-black"
            >
              <Instagram size={16} /> Scrivici in DM
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {merch.map((m, i) => (
              <MerchCard key={m.id} item={m} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Gallery />

      <Footer />
    </div>
  );
}
